<template>
  <div
    :class="`row m-0 align-items-center content-row ${rowSelectable ? 'row-select' : ''} ${ rowUnselected ? 'row-unselected' : '' } ${rowClass}`"
    :style="`background: ${bodyBgColor};`"
  >
    <div
      v-if="showCheckboxes"
      class="col flex-grow-0 py-0 content-cell checkbox"
    >
      <RsFormCheckbox
        :id="`${id}_data_table_row_check_${itemIndex}_${item.id}`"
        :model-value="isChecked"
        :true-value="true"
        :false-value="false"
        @change="onCheckboxChange($event, item)"
      />
    </div>
    <div
      v-for="(field, index) in (fields.filter((v) => v.variant !== 'hidden' && shouldShowField(v, item)))"
      :id="`${id}_data_table_cell_${field.variant}_col_${index}_row_${itemIndex}`"
      :key="`${index}_cell`"
      :colspan="field.colspan || 0"
      :class="`col content-cell ${field.variant} ${field.isCentered ? 'centered' : ''} ${field.colClass ? field.colClass(item) : ''}`"
      @click="handleRowSelect(item, field, itemIndex)"
    >
      <div class="text-truncate">
        <!-- VARIANT - FAVORITE -->
        <RsFormSwitch
          v-if="field.variant === 'favorite'"
          :id="`data_table_favorite_${item.id}`"
          v-model="item[field.property.replace('?custom', '')]"
          class="flex-grow-0"
          :true-value="field.trueValue"
          :false-value="field.falseValue"
          @change="onFavoriteChange($event, item)"
        />

        <!-- VARIANT - SWITCH -->
        <RsFormSwitch
          v-else-if="field.variant === 'switch'"
          :id="`${id}_data_table_switch_${(field.property.replace('?custom', ''))}_${itemIndex}`"
          v-model="item[field.property.replace('?custom', '')]"
          :true-value="field.trueValue"
          :false-value="field.falseValue"
          :disabled="field.disabled"
          @change="onSwitchChanged(field, item)"
        />

        <!-- VARIANT - CATEGORY -->
        <div
          v-else-if="field.variant === 'category'"
          class="d-flex justify-content-center"
          @click="handleRowSelect(item, field, itemIndex)"
        >
          <RsPill
            :variant="((field.variantOptions || []).find((v) => v.value === getPropertyValue(field, item)) || {}).subVariant"
            :title="getPropertyValue(field, item)"
          />
        </div>

        <!-- VARIANT - STATUS -->
        <div
          v-else-if="field.variant === 'status'"
          class="text-truncate"
          @click="handleRowSelect(item, field, itemIndex)"
        >
          <RsStatus :variant="getPropertyValue(field, item)" />
        </div>

        <!-- VARIANT - CLICKABLE -->
        <div v-else-if="field.variant === 'clickable'">
          <Component
            :is="field.useRouterLink ? 'router-link' : 'a'"
            :id="`${id}_data_table_${field.variant}_col_${index}_row_${itemIndex}_anchor`"
            :role="field.dynamicHref || field.href ? 'link' : 'button'"
            class="d-block clickable-text"
            :target="field.linkTarget"
            :title="!field.hideTitle ? getPropertyValue(field, item) : false"
            :to="field.dynamicHref || field.href ? getHref(item, field) : ''"
            :href="field.dynamicHref || field.href ? getHref(item, field) : false"
            @click="onItemClicked(item, field, itemIndex)"
          >
            <div
              class="text-truncate"
              v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`)"
            />

            <div
              v-if="field.hasSubText"
              class="sub-text text-truncate"
            >
              {{ getSubTextValue(field, item) }}
            </div>
          </Component>
        </div>

        <!-- VARIANT - AUDIO -->
        <div v-else-if="field.variant === 'audio'">
          <RsButton
            v-if="checkValue(field, item)"
            class="rs-button--rounded rs-button--light play-button"
            icon="play"
            @click="onAudioClicked(item, itemIndex)"
          >
            {{ getPropertyValue(field, item) }}
          </RsButton>
          <div
            v-else
            class="ml-3"
          >
            {{ getPropertyValue(field, item) }}
          </div>
        </div>

        <!-- VARIANT - DUPLICATE -->
        <div v-else-if="field.variant === 'duplicate'">
          <button
            class="clickable-text"
            @click="onItemClicked(item, field, itemIndex)"
          >
            <RsTooltip
              :target="`${id}_data_table_duplicate_${itemIndex}_${index}`"
              title="Duplicate Unit"
              position="left"
              :delay="{ show: 200 }"
            />
            <RsIcon
              :id="`${id}_data_table_duplicate_${itemIndex}_${index}`"
              weight="far"
              icon="clone"
            />
          </button>
        </div>

        <!-- VARIANT - DATE -->
        <div
          v-else-if="field.variant === 'date'"
          class="d-block text-truncate"
          @click="handleRowSelect(item, field, itemIndex)"
        >
          {{ getPropertyValue(field, item) | rsDateTime }}
        </div>

        <!-- VARIANT - RADIO -->
        <div v-else-if="field.variant === 'radio'">
          <RsFormRadio
            :id="`${id}_data_table_radio_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
            :model-value="field.radioModelValue(item)"
            :value="field.trueValue"
            @change="onRadioClicked(field, item, itemIndex)"
          />
        </div>

        <!-- VARIANT - BUTTON -->
        <RsButton
          v-else-if="field.variant === 'button'"
          :id="`${id}_data_table_action_button_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
          :variant="field.variantColour"
          :icon="field.variantIcon"
          style="height: 32px;"
          @click="$emit(`on-${field.property}-clicked`, item)"
        >
          {{ field.variantDisplayValue }}
        </RsButton>

        <!-- VARIANT - INPUT -->
        <!-- class="form-control" -->
        <input
          v-else-if="field.variant === 'input'"
          :id="`${id}_data_table_input_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
          :class="`form-control ${getPropertyClasses(field, item).join(' ')}`"
          :value="getPropertyValue(field, item)"
          :type="field.inputType"
          @change="onInputChanged(item, itemIndex, field, $event.target.value)"
        >

        <!-- VARIANT - SELECT -->
        <!-- class="form-control" -->
        <select
          v-else-if="field.variant === 'select'"
          :id="`${id}_data_table_select_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
          :class="`form-control ${getPropertyClasses(field, item).join(' ')}`"
          :value="getPropertyValue(field, item)"
          @change="onSelectChange(item, itemIndex, field, $event.target.value)"
        >
          <option
            v-for="opt in field.selectOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.text }}
          </option>
        </select>

        <!-- VARIANT - TEXT w/ ICON -->
        <div
          v-else-if="field.variant === 'text-icon'"
          class="text-icon"
        >
          <RsIcon
            weight="fas"
            :icon="field.dynamicIcon(item)"
          />
          <div
            :title="!field.hideTitle ? stripHTMLTags($options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)) : false"
            :class="{ 'd-block text-truncate': $options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`).length > 25 }"
            v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`)"
          />
        </div>

        <!-- VARIANT - PRICE -->
        <div
          v-else-if="field.variant === 'price'"
          class="d-block text-truncate w-100"
          :style="{ maxWidth: (field.colspan || 0 > 1) ? '100%' : '' }"
        >
          ${{ Number.parseFloat(getPropertyValue(field, item)).toFixed(2) }}
        </div>

        <!-- VARIANT - PERCENTAGE (DEFAULT) -->
        <div
          v-else-if="field.variant === 'percentage'"
          class="d-block text-truncate w-100"
          :style="{ maxWidth: (field.colspan || 0 > 1) ? '100%' : '' }"
        >
          <div
            :title="!field.hideTitle ? `${stripHTMLTags($options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true))} %` : false"
            :class="{ 'd-block text-truncate': $options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`).length > 25 }"
            v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item) || 0}%`, true)"
          />
        </div>

        <!-- VARIANT - TEXT (DEFAULT) -->
        <div
          v-else
          class="d-block text-truncate w-100"
          :style="{ maxWidth: (field.colspan || 0 > 1) ? '100%' : '' }"
        >
          <div
            :title="!field.hideTitle ? stripHTMLTags($options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)) : false"
            :class="{ 'd-block text-truncate': $options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`).length > 25 }"
            v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)"
          />
        </div>
      </div>
    </div>

    <div
      v-if="hasDetailsView"
      class="col flex-grow-0 content-cell details-view"
    >
      <button
        :id="`${id}_data_table_details_view_${itemIndex}`"
        class="btn-details-view rs-text-dark"
        @click="onDetailsViewToggle(itemIndex, item)"
      >
        <i
          v-if="detailsViewType === 'accordion'"
          class="fas fa-chevron-down"
          :class="{ 'fa-rotate-180': isIndexSelected(itemIndex) }"
        />
        <i
          v-else
          :class="{ 'fas fa-plus': !isIndexSelected(itemIndex), 'fas fa-minus': isIndexSelected(itemIndex) }"
        />
      </button>
    </div>

    <div
      v-if="hasDropdown"
      class="col flex-grow-0 content-cell dropdown"
    >
      <RsDropdownActionMenu
        :id="`${id}_data_table_dropdown_${itemIndex}`"
        icon="ellipsis-v"
        right
        action-menu
        :options="dropdownOptions"
      />
    </div>

    <div
      v-if="isIndexSelected(itemIndex)"
      :id="`${id}_details_view_${itemIndex}`"
      ref="detailsView"
      class="col-12 p-0 details-view-container"
      :style="`cursor: default;`"
    >
      <slot
        :item="item"
        :index="itemIndex"
        :name="detailSlotMapName"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import {
  IDataTableFieldConfig,
  IDropdownOptions,
} from '@/shared';

export default defineComponent({
  name: 'RsPartialTableRow',

  props: {
    rowSelectable: {
      type: Boolean,
      default: false,
    },

    rowUnselected: {
      type: Boolean,
      default: false,
    },

    fields: {
      type: Array as PropType<IDataTableFieldConfig[]>,
      default: () => [],
    },

    showCheckboxes: {
      type: Boolean,
      default: false,
    },

    bodyBgColor: {
      type: String,
      default: '#fff',
    },

    isChecked: {
      type: Boolean,
      default: false,
    },

    hasDetailsView: {
      type: Boolean,
      default: false,
    },

    hasDropdown: {
      type: Boolean,
      default: false,
    },

    detailsViewType: {
      type: String,
      default: 'accordion',
      validator: (val: string) => ['accordion', 'toggle'].includes(val),
    },

    id: String,
    itemIndex: Number,
    item: {
      type: Object as PropType<Record<string, any>>,
    },

    detailSlotMapName: {
      type: String,
      default: '',
    },
    dropdownOptions: {
      type: Array as PropType<IDropdownOptions[]>,
      default: () => [],
    },

    getPropertyValue: Function as PropType<(field: IDataTableFieldConfig, item: Record<string, any>) => string>,

    getHref: Function as PropType<(item: Record<string, any>, field: IDataTableFieldConfig) => string>,

    getSubTextValue: Function as PropType<(field: IDataTableFieldConfig, item: Record<string, any>) => string>,

    checkValue: Function as PropType<(field: IDataTableFieldConfig, item: Record<string, any>) => boolean>,

    getPropertyClasses: Function as PropType<(field: IDataTableFieldConfig, item: Record<string, any>) => string[]>,

    isIndexSelected: Function as PropType<(index: number) => boolean>,

    stripHTMLTags: Function as PropType<(html: string) => string>,
  },

  computed: {
    rowClass(): string {
      const s = this.fields.find((field) => !!field.rowClass);
      return (s && this.item && s.rowClass) ? s.rowClass(this.item) : '';
    },
  },

  methods: {
    shouldShowField(field: IDataTableFieldConfig, item: Record<string, any>) {
      if (field.dynamicIsHidden) {
        return !field.dynamicIsHidden(item);
      }

      if (field.variant === 'dropdown') {
        return false;
      }

      return true;
    },

    handleRowSelect(item: Record<string, any>, field: IDataTableFieldConfig, index: number) {
      if (field.onColClick) field.onColClick(item);

      this.$emit('on-row-selected', { item, field, index });

      return { item, field, index };
    },

    onCheckboxChange(value: boolean, item: Record<string, any>) {
      this.$emit('on-checkbox-change', { value, item });
      return { value, item };
    },

    onFavoriteChange(event: Event, item: Record<string, any>) {
      this.$emit('on-favorite-change', { event, item });
      return { event, item };
    },

    onSwitchChanged(field: IDataTableFieldConfig, item: Record<string, any>) {
      this.$emit('on-switch-change', { field, item });
      return { field, item };
    },

    onItemClicked(item: Record<string, any>, field: IDataTableFieldConfig, index: any) {
      this.$emit('on-item-click', { field, item, index });
      return { field, item, index };
    },

    onAudioClicked(item: Record<string, any>, index: number) {
      this.$emit('on-audio-click', { item, index });
      return { item, index };
    },

    onInputChanged(item: Record<string, any>, itemIndex: number, fieldName: IDataTableFieldConfig, newValue: string) {
      this.$emit('on-input-change', { item, itemIndex, fieldName, newValue });
      return { item, itemIndex, fieldName, newValue };
    },

    onRadioClicked(field: IDataTableFieldConfig, item: Record<string, any>, itemIndex: number) {
      this.$emit('on-radio-click', { item, field, itemIndex });
      return { item, field, itemIndex };
    },

    onSelectChange(item: Record<string, any>, itemIndex: number, field: IDataTableFieldConfig, value: string) {
      this.$emit('on-select-change', { item, field, itemIndex, value });
      return { item, field, itemIndex, value };
    },

    onDetailsViewToggle(index: number, item: Record<string, any>) {
      this.$emit('on-details-view-toggle', { index, item });
      return { index, item };
    },
  },
});
</script>
